import React, { FunctionComponent, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { parseISO, format } from 'date-fns';

import Layout from '../../containers/Layout';
import ImageTitle, { Icons } from '../../components/ImageTitle';
import Button from '../../components/Button';
import { Options, Content, ExpirationDate, Barcode, Div } from './styles';
import { useAlert } from '../../containers/Alert';
import { useOrder } from '../../hooks/order/useOrder';
import AlertConstants from '../../constants/alert.constant';
import {
  PaymentType,
  Order,
} from '../../requests/caipirinha-api/interfaces/order.interface';
import CaipirinhaRequest from '../../requests/caipirinha-api/caipirinha.request';
import { useReviewPaymentGtm } from '../../hooks/gtm/reviewPayment/useReviewPaymentGtm';
import LgpdCard from '../../components/LgpdCard';
import IcCopy from '../../assets/icons/ic_copy.svg';
import IcDownload from '../../assets/icons/ic_download.svg';

const Boleto: FunctionComponent = () => {
  const { alert } = useAlert();
  const { order, lgpd, getOrderByIdAndDocument, confirmLoading } = useOrder();
  const { id, document } = useParams<{ id: string; document: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [orderPayment, setOrderPayment] = useState<Order>();
  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
  const [boleto, setBoleto] = useState<string | undefined>();
  const { sendDataToGtm: sendReviewPaymentDataToGtm } = useReviewPaymentGtm();

  const { location } = useHistory();
  const locationState: any = location.state;

  const getBarcode = (): string => {
    if (orderPayment?.details?.boleto?.barcode) {
      return orderPayment.details.boleto.barcode;
    }
    return '';
  };

  const getDownloadData = async (orderData: Order) => {
    if (orderData?.details?.payments?.length > 0 &&
      String(orderData.details.payments[0].id) === PaymentType.BOLETO
    ) {
      setLoadingDownload(true);
      const response = await CaipirinhaRequest.getBoleto(orderData);
      if (response) {
        setBoleto(`data:application/pdf;base64,${response}`);
      }
    }
    setLoadingDownload(false);
  };

  useEffect(() => {
    if (!confirmLoading) setLoading(confirmLoading);
  }, [confirmLoading]);

  useEffect(() => {
    if (locationState?.resultPayment) {
      setOrderPayment(locationState.resultPayment);
      sendReviewPaymentDataToGtm(locationState.resultPayment);
      setLoading(false);
    } else {
      getOrderByIdAndDocument(id, document);
    }
  }, [id]);

  useEffect(() => {
    if (order) {
      setOrderPayment(order);
      sendReviewPaymentDataToGtm(order);
      setLoading(false);
    }
  }, [order]);

  useEffect(() => {
    if (orderPayment !== undefined) {
      getDownloadData(orderPayment);
    }
  }, [orderPayment]);

  const getExpirationDate = (): string => {
    if (orderPayment?.details?.boleto?.expirationDate) {
      const expirationDate = parseISO(
        orderPayment.details.boleto.expirationDate,
      ).setUTCHours(3);
      return format(expirationDate, 'dd/MM/yyy');
    }
    return '';
  };

  const copyBarcode = (): void => {
    const barcode = getBarcode();
    if (barcode) {
      navigator.clipboard.writeText(barcode);
      alert(AlertConstants.copiedBarcode);
    }
  };

  return (
    <Layout dark loading={loading || loadingDownload}>
      {orderPayment && (
        <>
          <ImageTitle
            data-cy='bank-slip'
            icon={Icons.boletoSuccess}
            text="Seu boleto foi gerado!"
            primary
          />
          <Content data-cy='date-expitarion-text'>Data de expiração</Content>
          <Div>
            <ExpirationDate data-cy='expiration-date'>{getExpirationDate() || 'N/A'}</ExpirationDate>
          </Div>
          <Div>
            <Barcode data-cy='barcode'>{getBarcode() || 'Código de barras indisponível'}</Barcode>
          </Div>
          <Options>
            <Button
              data-cy='copy-barcode-button'
              theme="MINIMAL"
              onClick={copyBarcode}
              icon={IcCopy}
            >
              Copiar Código de Barras
            </Button>
            {boleto && (
              <>
                <br />
                <Button
                  data-cy='download-button'
                  theme="MINIMAL"
                  icon={IcDownload}
                  loading={loadingDownload}
                  download={`boleto-${orderPayment.id}.pdf`}
                  href={boleto}
                >
                  Fazer Download
                </Button>
              </>
            )}
          </Options>
        </>
      )}
      {lgpd?.self && <LgpdCard url={lgpd.self.offerPolicy} />}
    </Layout>
  );
};

export default Boleto;
